// @ts-check
import qs from "qs";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useOrganization } from "../OrganizationContext";
import { useApi } from "../shared/useApi";

/** @typedef {("active" | "archived")} ModelStatus */

/**
 * @typedef ModelProps
 * @property {string} [_id]
 * @property {string} [oid]
 * @property {ModelStatus} status
 * @property {string} value
 * @property {string} label
 */

/** @typedef {import("../shared/AuditHelpers").AuditedResource & ModelProps} Model */

export const MODELS = "models";

/** @type {(searchParams?: import("../shared/QueryHelpers").SearchParams<Model>) => import("react-query").UseQueryResult<import("../shared/QueryHelpers").Page<Model>>} */
export const useModelsQuery = (searchParams = {}) => {
  const api = useApi();
  const { selectedOrganization } = useOrganization();
  return useQuery([MODELS, selectedOrganization, searchParams], async () => {
    const { limit = 0, skip = 0, sort = "value", ...query } = searchParams;
    const response = await api.get(`v1/models`, {
      searchParams: qs.stringify({ limit, skip, sort, ...query }),
    });
    const totalCount = Number(response.headers.get("X-Total-Count"));
    const list = await response.json();
    return {
      totalCount,
      list,
    };
  });
};

/** @type {(modelId: string) => import("react-query").UseQueryResult<Model>} */
export const useModelByIdQuery = (modelId) => {
  const api = useApi();
  const { selectedOrganization } = useOrganization();
  return useQuery([MODELS, selectedOrganization, modelId], async () => {
    return api.get(`v1/models/${modelId}`).json();
  });
};

export const useFetchModels = () => {
  const api = useApi();
  return async (searchParams) => {
    return api.get(`v1/models`, { searchParams: qs.stringify(searchParams) }).json();
  };
};

/** @type {(value: string) => Model} */
export const createModel = (value) => {
  const sanitizedValue = value.trim();
  return {
    value: sanitizedValue,
    label: sanitizedValue,
    status: "active",
  };
};

/** @type {() => import("react-query").UseMutationResult<Model[], unknown, Model[]>} */
export const useUpsertManyModelMutation = () => {
  const { mutateAsync: upsertModel } = useUpsertModelMutation();
  return useMutation(async (models) => {
    return Promise.all(models.map((model) => upsertModel(model)));
  });
};

/** @type {() => import("react-query").UseMutationResult<Model, unknown, Model>} */
export const useUpsertModelMutation = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    async (model) => {
      if (model._id) {
        return api.patch(`v1/models/${model._id}`, { json: model }).json();
      }
      return api.post(`v1/models`, { json: model }).json();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(MODELS);
      },
    }
  );
};
